import { isAdmin, type Me } from '@supply/contracts'

import type { Member } from '../types'
import type { MockEngine } from './engine'

/**
 * Modo "dados fictícios" (SIS-465): só o dev admin liga, só nesta aba.
 *
 * Esta é a parte leve, carregada por todos: guarda o pedido no
 * `sessionStorage` da aba e diz se o modo está ARMADO. O gerador e o banco em
 * memória (`engine.ts`) só baixam com `import()` quando um admin arma o modo.
 *
 * O pedido no `sessionStorage` sozinho não liga nada: quem arma é a sessão,
 * depois de ler o papel REAL no banco (`armMock` recusa quem não é admin).
 * Armado, `data/api.ts`, `integration-http.ts` e o chamado respondem pelo
 * motor em memória — nenhuma chamada de dados sai do navegador. Fechar a aba,
 * sair ou desligar limpa tudo; recarregar a página refaz o cenário do zero.
 */

const STORAGE_KEY = 'supply:dados-ficticios'

/** Semente fixa: o mesmo cenário a cada vez (para repetir o teste). */
export const MOCK_SEED = 465

export const MOCK_LABEL = 'Dados fictícios'
export const MOCK_UNAVAILABLE = 'Indisponível com dados fictícios.'
/** "Ver como" fica desligado no modo (decisão do SIS-465, ver docs/ops). */
export const VIEW_AS_MOCK_BLOCKED = 'Indisponível com dados fictícios: desative o modo para usar o "ver como".'

let engine: MockEngine | null = null

// Só em dev: o HMR reexecuta este módulo; sem isto o motor sumiria com a aba
// ainda no modo e as telas voltariam a ler (e gravar) no banco de verdade.
if (import.meta.hot?.data) {
  engine = (import.meta.hot.data as { engine?: MockEngine | null }).engine ?? null
  import.meta.hot.dispose((data: { engine?: MockEngine | null }) => {
    data.engine = engine
  })
}

/** O admin pediu o modo nesta aba (ainda não quer dizer que está armado). */
export function mockRequestedFor(memberId: string): boolean {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    const parsed = raw ? (JSON.parse(raw) as { memberId?: unknown }) : null
    return parsed?.memberId === memberId
  } catch {
    return false
  }
}

export function hasMockRequest(): boolean {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) !== null
  } catch {
    return false
  }
}

/** Liga para a próxima carga da aba. Só admin (a sessão confere de novo ao armar). */
export function requestMock(me: Me): boolean {
  if (!isAdmin(me)) return false
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ memberId: me.memberId }))
    return true
  } catch {
    return false
  }
}

export function clearMockRequest(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* nada a limpar */
  }
}

/** A sessão deve armar o modo? Papel lido do banco, sem "ver como", com pedido desta pessoa. */
export function shouldArmMock(me: Me, viewingAs: boolean): boolean {
  return isAdmin(me) && !viewingAs && mockRequestedFor(me.memberId)
}

/**
 * Arma o modo: baixa o motor e monta o cenário com o admin dentro (como
 * fantasma, igual ao real). Recusa quem não é admin — mesmo com o pedido no
 * `sessionStorage`, o app segue com os dados reais.
 */
export async function armMock(admin: Member, me: Me, today: string): Promise<boolean> {
  if (!isAdmin(me) || me.memberId !== admin.id) {
    clearMockRequest()
    return false
  }
  const { createMockEngine } = await import('./engine')
  engine = createMockEngine({ seed: MOCK_SEED, today, admin })
  return true
}

/** Desarma (sair, desligar). Os dados fictícios somem da memória. */
export function disarmMock(): void {
  engine = null
}

export function isMockActive(): boolean {
  return engine !== null
}

export function mockEngine(): MockEngine | null {
  return engine
}

/** Só para testes: arma com um motor já criado. */
export function setMockEngineForTests(next: MockEngine | null): void {
  engine = next
}
